import React, { useEffect, useState } from 'react';
import {
  AIConversation,
  AIConversationSearchType,
  AIConversationStatus,
} from 'v2/models';
import documentChatIcon from 'v2/icons/document_chat.svg';
import drmChatIcon from 'v2/icons/drm_chat.svg';
import { BeatLoader } from 'react-spinners';
import styles from './aiSearchItem.module.scss';
import classnames from 'classnames';
import { BsTrash, BsCheckLg, BsXLg, BsPen } from 'react-icons/bs';
import restFactory from 'v2/utils/restFactory';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useOutsideClick from 'v2/hooks/useOutsideClick';
import { AI_SEARCH_POLL_INTERVAL } from 'utils/constants';
import { isAiSearchRunning } from 'v2/utils/AISearchUtils';
import { aiConversationKeys } from 'v2/queries';

type StateTypes = 'newName' | 'removeSearch' | 'showConfirmation';
interface AISearchItemProps {
  aiConversation: AIConversation;
  isSelected?: boolean;
  refetchConversations: () => void;
  handleChangeOfName: (isChange: boolean) => void;
}

const CurrentLeftIcon = ({
  removeSearch,
  isSelected,
  aiConversation,
}: {
  removeSearch: boolean;
  isSelected?: boolean;
  aiConversation: AIConversation;
}) => {
  if (removeSearch && isSelected) {
    return (
      <div className={styles.leftIcon}>
        <BsTrash />
      </div>
    );
  }

  const iconByType =
    aiConversation.search_type === AIConversationSearchType.DOCUMENT
      ? { icon: documentChatIcon, alt: 'document icon' }
      : { icon: drmChatIcon, alt: 'drm icon' };

  return (
    <div className={styles.leftIcon}>
      {[AIConversationStatus.IN_PROCESS, AIConversationStatus.QUEUED].includes(
        aiConversation.status
      ) ? (
        <BeatLoader size={4} data-testid="loader-icon" />
      ) : (
        <img src={iconByType.icon} alt={iconByType.alt} />
      )}
    </div>
  );
};

const CurrentRightButtons = ({
  showConfirmation,
  isSelected,
  editName,
  handleStateChanges,
  handleOnClickCheck,
}: {
  showConfirmation: boolean;
  isSelected?: boolean;
  editName: boolean;
  handleStateChanges: (stateType: StateTypes, value: boolean) => void;
  handleOnClickCheck: () => void;
}) => {
  if (showConfirmation && isSelected) {
    return (
      <div
        className={classnames(styles.rightButtons, {
          [styles.editName]: editName,
        })}
      >
        <BsCheckLg
          size={15}
          className="mr-1"
          onClick={handleOnClickCheck}
          data-testid="check_icon"
          role="check_confirmation"
        />
        <BsXLg
          size={15}
          onClick={() => handleStateChanges('showConfirmation', false)}
          data-testid="close_icon"
          role="close_confirmation"
        />
      </div>
    );
  } else if (isSelected) {
    return (
      <div className={styles.rightButtons}>
        <BsPen
          onClick={() => handleStateChanges('newName', true)}
          data-testid="edit_icon"
        />
        <BsTrash
          onClick={() => handleStateChanges('removeSearch', true)}
          data-testid="remove_icon"
          role="remove"
        />
      </div>
    );
  }

  return null;
};

const AISearchItem = ({
  aiConversation,
  isSelected,
  refetchConversations,
  handleChangeOfName,
}: AISearchItemProps) => {
  const queryClient = useQueryClient();
  const REF_ID = `ai_conversation_item_${aiConversation.id}`;
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [editName, setEditName] = useState(false);
  const [newName, setNewName] = useState(aiConversation.name);
  const [removeSearch, setRemoveSearch] = useState(false);

  const states_table: Record<StateTypes, (value: boolean) => void> = {
    newName: function (value) {
      setEditName(value);
    },
    removeSearch: function (value) {
      setRemoveSearch(value);
    },
    showConfirmation: function (value) {
      setShowConfirmation(value);
    },
  };

  useQuery(
    aiConversationKeys.detail(aiConversation.id),
    async () =>
      await restFactory.get<AIConversation>(
        `/dj/api/v1/ai_conversation/${aiConversation.id}/`
      ),
    {
      cacheTime: 0,
      enabled: isAiSearchRunning(aiConversation),
      refetchOnMount: false,
      refetchInterval: (data?: AIConversation) => {
        return isAiSearchRunning(data) ? AI_SEARCH_POLL_INTERVAL : false;
      },
      onSuccess: (data: AIConversation) => {
        if (data.status !== aiConversation.status) {
          queryClient.invalidateQueries('AIConversationList');
        }
      },
    }
  );

  useEffect(() => {
    setShowConfirmation(editName || removeSearch);
  }, [editName, removeSearch]);

  useEffect(() => {
    if (aiConversation.name !== newName) {
      setNewName(aiConversation.name);
    }
  }, [aiConversation.name]);

  useEffect(() => {
    if (showConfirmation) return;
    if (editName) {
      setEditName(false);
      setNewName(aiConversation.name);
    }
    if (removeSearch) {
      setRemoveSearch(false);
    }
  }, [showConfirmation]);

  const handleClickOutside = () => {
    setShowConfirmation(false);
  };

  const ref = useOutsideClick(handleClickOutside, REF_ID);

  const { mutate: saveName } = useMutation(
    async () =>
      await restFactory.patch<AIConversation>(
        `/dj/api/v1/ai_conversation/${aiConversation.id}/`,
        {
          name: newName,
        }
      ),
    {
      onSuccess: () => {
        setEditName(false);
        handleChangeOfName(true);
        refetchConversations();
      },
      onError: () => {
        toast.error(`Failed to update search's name`);
      },
    }
  );

  const { mutate: deleteSearchItem } = useMutation(
    async () => {
      await restFactory.delete(
        `/dj/api/v1/ai_conversation/${aiConversation.id}/`
      );
    },
    {
      onSuccess: async () => {
        setRemoveSearch(false);
        refetchConversations();
      },
      onError: () => {
        toast.error(
          'An error occurred while deleting the search. Please contact support'
        );
      },
    }
  );

  const handleOnClickCheck = () => {
    if (editName) {
      if (newName === aiConversation.name || newName === '') {
        setEditName(false);
        newName === '' && setNewName(aiConversation.name);
      } else {
        saveName();
      }
    } else if (removeSearch) {
      deleteSearchItem();
    }
  };

  const handleOnEnter = (event?: React.KeyboardEvent<HTMLInputElement>) => {
    if (event?.key === 'Enter') {
      handleOnClickCheck();
    }
  };

  const handleStateChanges = (stateType: StateTypes, value: boolean) => {
    states_table[stateType](value);
  };

  return (
    <div className={classnames(REF_ID, styles.main)} ref={ref}>
      <CurrentLeftIcon
        isSelected={isSelected}
        removeSearch={removeSearch}
        aiConversation={aiConversation}
      />
      {editName ? (
        <input
          type="text"
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          onFocus={(event) => event.target.select()}
          onKeyDown={handleOnEnter}
          className={styles.editNameInput}
          autoFocus
          role="textbox"
        />
      ) : (
        <>
          <span className={styles.buttonText}>
            {removeSearch
              ? `Delete "${aiConversation.name}"`
              : aiConversation.name}
          </span>
          {!isSelected && <div className="shadow_button"></div>}
        </>
      )}

      {isSelected && (
        <CurrentRightButtons
          editName={editName}
          isSelected={isSelected}
          handleOnClickCheck={handleOnClickCheck}
          showConfirmation={showConfirmation}
          handleStateChanges={handleStateChanges}
        />
      )}
    </div>
  );
};

export default AISearchItem;
